import type { CSSProperties } from "react";

import { CheckmarkIcon, IconWeight } from "@inno-ui/icons";
import { Skeleton, SkeletonVariants } from "@inno-ui/skeleton";
import { Tooltip, TooltipPlacements } from "@inno-ui/tooltip";
import clsx from "clsx";
import { addMinutes, format, startOfDay } from "date-fns";
import { de, enUS, es, fr, it, pt, zhCN } from "date-fns/locale";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { Language } from "../../../../utils/enums";
import { groupTimeframesByLocalDay, toLocalTimeframes } from "../../../../utils/helpers/smart-scheduling";
import {
  buildQuarterHourTicksForDay,
  countSlotOverlaps,
  pad2,
  toLocalDayDate,
} from "./timeslots-visibility.helpers";
import styles from "./timeslots-visibility.module.scss";

type Timeframe = {
  start_at_utc: string;
  end_at_utc: string;
  start_at: string;
  end_at: string;
};

type Availability = {
  start_at_utc: string;
  end_at_utc: string;
};

type Props = {
  apiTimezone?: string;
  timeframes: Timeframe[];
  availabilities?: Availability[];
  isLoading?: boolean;
  selectedSlotStarts?: Set<string>;
  onToggleSlot?: (slotStartIso: string) => void;
};

const TAKEN_OPACITY_STEPS = [0, 0.06, 0.12, 0.18, 0.24];
const MAX_TAKEN_LEVEL = 4;

export function TimeslotsVisibility({
  timeframes,
  availabilities,
  isLoading,
  selectedSlotStarts,
  onToggleSlot,
}: Props) {
  const { t, i18n } = useTranslation();

  // Get locale for date formatting
  const getDateLocale = () => {
    const currentLang = i18n.language as Language;
    switch (currentLang) {
      case Language.German:
        return de;
      case Language.French:
        return fr;
      case Language.Spanish:
        return es;
      case Language.Italian:
        return it;
      case Language.Portuguese:
        return pt;
      case Language.Chinese:
        return zhCN;
      case Language.English:
      default:
        return enUS;
    }
  };

  const localTimeframes = useMemo(() => toLocalTimeframes(timeframes), [timeframes]);
  const timeframesByDay = useMemo(() => groupTimeframesByLocalDay(localTimeframes), [localTimeframes]);
  const localAvailabilities = useMemo(
    () => toLocalTimeframes(availabilities ?? []),
    [availabilities],
  );
  const availabilitiesByDay = useMemo(
    () => groupTimeframesByLocalDay(localAvailabilities),
    [localAvailabilities],
  );

  const days = useMemo(() => {
    return Array.from(timeframesByDay.keys())
      .sort()
      .map(key => ({ key, date: toLocalDayDate(key) }));
  }, [timeframesByDay]);

  if (days.length === 0) {
    return (
      <div className={styles.timeslotsVisibilityWrapper}>
        <div className={styles.emptyState}>No timeframes available.</div>
      </div>
    );
  }

  // loading skeleton
  if (isLoading) {
    return (
      <div className={styles.timeslotsVisibilityWrapper}>
        <div className={styles.timeslotsVisibilityContainer}>
          <div className={styles.timeslotsVisibilityItem}>
            <Skeleton height={26} variant={SkeletonVariants.Rectangular} count={10} />
          </div>
          <div className={styles.timeslotsVisibilityItem}>
            <Skeleton height={26} variant={SkeletonVariants.Rectangular} count={10} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.timeslotsVisibilityWrapper}>
      <div className={styles.columns}>
        {days.map(({ key, date }) => {
          const dayTimeframes = timeframesByDay.get(key) ?? [];
          const dayAvailabilities = availabilitiesByDay.get(key) ?? [];
          const { ticks: quarterHourTicks, rangeStarts } = buildQuarterHourTicksForDay(dayTimeframes);
          const dayStart = startOfDay(date);
          const availabilityCountsByMinute = new Map<number, number>();

          for (const minute of quarterHourTicks) {
            const slotStart = addMinutes(dayStart, minute);
            const count = countSlotOverlaps(slotStart, dayAvailabilities);
            availabilityCountsByMinute.set(minute, count);
          }

          return (
            <div key={key} className={styles.dayColumn}>
              <div className={styles.dayHeader}>
                <div className={styles.dayName}>{format(date, "EEEE", { locale: getDateLocale() })}</div>
                <div className={styles.dayDate}>{format(date, "dd.MM.yyyy", { locale: getDateLocale() })}</div>
              </div>

              <div className={styles.dayBody}>
                {quarterHourTicks.map((minute) => {
                  // Show label on full hours and also on the first tick of each timeframe range.
                  const showLabel = minute % 60 === 0 || rangeStarts.has(minute);
                  const hours = Math.floor(minute / 60);
                  const mins = minute % 60;
                  const timeLabel = `${pad2(hours)}:${pad2(mins)}`;

                  const slotStart = addMinutes(dayStart, minute);
                  const slotStartIso = slotStart.toISOString();
                  const selected = selectedSlotStarts?.has(slotStartIso) ?? false;
                  const availabilityCount = availabilityCountsByMinute.get(minute) ?? 0;
                  const availabilityLevel = Math.min(availabilityCount, MAX_TAKEN_LEVEL);
                  const availabilityOpacity = TAKEN_OPACITY_STEPS[availabilityLevel] ?? 0;

                  return (
                    <div key={minute} className={styles.row}>
                      <div className={styles.timeLabel}>{showLabel ? timeLabel : ""}</div>
                      <Tooltip
                        zIndex={10}
                        placement={TooltipPlacements.RightEnd}
                        overlay={
                          availabilityCount > 0
                            ? (<div>{availabilityCount === 1 ? t("This slot has been chosen by {{count}} other participant", { count: availabilityCount }) : t("This slot has been chosen by {{count}} other participants", { count: availabilityCount })}</div>)
                            : undefined
                        }
                      >
                        <button
                          type="button"
                          className={clsx(
                            styles.slot,
                            styles.available,
                            availabilityCount > 0 && !selected ? styles.taken : "",
                            selected ? styles.selected : "",
                          )}
                          style={availabilityCount > 0 && !selected
                            ? { "--availability-opacity": String(availabilityOpacity) } as CSSProperties
                            : undefined}
                          onClick={() => onToggleSlot?.(slotStartIso)}
                          aria-pressed={selected}
                        >
                          {selected && (
                            <CheckmarkIcon
                              color="var(--white)"
                              weight={IconWeight.Bold}
                            />
                          )}
                        </button>
                      </Tooltip>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
